import * as styles from '../FirstResponder.module.css';
import {
  LOCKED_RANKING_NOTE,
  LOCKED_RANKING_ORDER,
} from '../LivePreview/previewRankingPolicy';
import type { LiveRankingPolicy } from '../LivePreview/liveViewModel';
import {
  activeFirstNote,
  rankingVersionLabel,
  ranksOnActivity,
  VERSION_ONE_EXPLAINER,
  VERSION_THREE_STEPS,
} from '../Live/rankingCopy';

/*
 * The ranking order of the version the active policy PINS, read from the server — never fetched
 * from a constant that assumes one. Read-only: the order is a reviewed ranking-policy version,
 * never an Admin setting, and this page has no control that changes it.
 *
 *   - version three (its lead ranks on activity): the active-first steps (rankingCopy.ts);
 *   - version two: the locked ten-step list, imported unchanged from `previewRankingPolicy.ts`
 *     (the same list SettingsEditor.tsx renders);
 *   - version one: its own shorter tier-first order;
 *   - nothing readable: says so, rather than showing an order the server did not confirm.
 */

type Props = { ranking: LiveRankingPolicy | null | undefined };

const Note = ({ children }: { children: string }) => (
  <div
    className={`${styles.muted} ${styles.small}`}
    style={{ marginTop: 8 }}
    data-testid="flp-ranking-locked-note"
  >
    {children}
  </div>
);

const VersionLine = ({ ranking }: { ranking: LiveRankingPolicy }) => (
  <div
    className={`${styles.muted} ${styles.small}`}
    style={{ marginBottom: 6 }}
    data-testid="flp-ranking-version"
  >
    Pinned ranking: {rankingVersionLabel(ranking)}
  </div>
);

const SimpleList = ({ steps }: { steps: readonly string[] }) => (
  <ol className={styles.rankingList} data-testid="flp-ranking-order">
    {steps.map((step) => (
      <li key={step} className={styles.rankingLocked}>
        {step}
      </li>
    ))}
  </ol>
);

const RankingOrderList = ({ ranking }: Props) => {
  if (!ranking) {
    return (
      <Note>
        The pinned ranking version could not be read from the server, so no
        order is shown.
      </Note>
    );
  }

  if (ranksOnActivity(ranking)) {
    return (
      <>
        <VersionLine ranking={ranking} />
        <SimpleList steps={VERSION_THREE_STEPS} />
        <Note>{activeFirstNote(ranking)}</Note>
      </>
    );
  }

  if (ranking.versionNumber === 1) {
    return (
      <>
        <VersionLine ranking={ranking} />
        <SimpleList steps={VERSION_ONE_EXPLAINER.steps} />
        <Note>{VERSION_ONE_EXPLAINER.scoreNote}</Note>
      </>
    );
  }

  return (
    <>
      <VersionLine ranking={ranking} />
      <ol className={styles.rankingList} data-testid="flp-ranking-order">
        {LOCKED_RANKING_ORDER.map((step) => (
          <li
            key={step.key}
            className={styles.rankingLocked}
            data-testid={
              step.unavailable ? 'flp-preferred-pool-row' : undefined
            }
          >
            {step.label}
            {step.note ? (
              step.unavailable ? (
                <>
                  {' — '}
                  <em>{step.note}</em>
                </>
              ) : (
                <>
                  {' '}
                  <em>({step.note})</em>
                </>
              )
            ) : null}
          </li>
        ))}
      </ol>
      <Note>{LOCKED_RANKING_NOTE}</Note>
    </>
  );
};

export default RankingOrderList;
